import React, { useState, useEffect } from 'react';
import { X, StickyNote, Trash2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTE_COLORS = [
  { id: 'yellow', name: 'Yellow', bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800' },
  { id: 'blue', name: 'Blue', bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' },
  { id: 'red', name: 'Red', bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800' },
  { id: 'green', name: 'Green', bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800' },
  { id: 'purple', name: 'Purple', bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' },
];

const DayNoteModal = ({ date, onClose, onNoteSaved }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [selectedColor, setSelectedColor] = useState('yellow');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Format date for display
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Load existing notes for this date
  useEffect(() => {
    const loadNotes = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/day-notes/${date}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setNotes(data);
        }
      } catch (err) {
        console.error('Error loading notes:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadNotes();
  }, [date]);

  // Save new note
  const handleSaveNote = async () => {
    if (!newNote.trim()) return;
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/day-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: date,
          note: newNote.trim(),
          color: selectedColor
        })
      });
      
      if (res.ok) {
        const savedNote = await res.json();
        setNotes(prev => [...prev, savedNote]);
        setNewNote('');
        if (onNoteSaved) onNoteSaved();
      }
    } catch (err) {
      console.error('Error saving note:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/day-notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setNotes(prev => prev.filter(n => n.id !== noteId));
        if (onNoteSaved) onNoteSaved();
      }
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const getColorClasses = (colorId) => {
    return NOTE_COLORS.find(c => c.id === colorId) || NOTE_COLORS[0];
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full sm:w-[420px] sm:rounded-2xl rounded-t-2xl modal-shell-h overflow-hidden shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-yellow-50 to-amber-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-xl">
              <StickyNote size={20} className="text-yellow-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Day Notes</h2>
              <p className="text-xs text-gray-500">{formatDate(date)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Existing Notes */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : notes.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Existing Notes</p>
              {notes.map(note => {
                const colors = getColorClasses(note.color);
                return (
                  <div
                    key={note.id}
                    className={`p-3 rounded-xl border-2 ${colors.bg} ${colors.border} relative group`}
                  >
                    <p className={`text-sm ${colors.text} whitespace-pre-wrap`}>{note.note}</p>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>By {note.created_by_name}</span>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/50 rounded transition-all"
                        title="Delete note"
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <StickyNote size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No notes for this day yet</p>
            </div>
          )}

          {/* Add New Note */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Add New Note</p>
            
            {/* Color Picker */}
            <div className="flex gap-2">
              {NOTE_COLORS.map(color => (
                <button
                  key={color.id}
                  onClick={() => setSelectedColor(color.id)}
                  className={`w-8 h-8 rounded-full ${color.bg} border-2 ${
                    selectedColor === color.id ? 'border-gray-800 ring-2 ring-offset-2 ring-gray-400' : color.border
                  } transition-all`}
                  title={color.name}
                />
              ))}
            </div>
            
            {/* Note Input */}
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note for this day..."
              className={`w-full p-3 rounded-xl border-2 ${getColorClasses(selectedColor).bg} ${getColorClasses(selectedColor).border} ${getColorClasses(selectedColor).text} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 resize-none`}
              rows={3}
              data-testid="day-note-input"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={handleSaveNote}
            disabled={!newNote.trim() || isSaving}
            className="w-full py-3 px-4 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
            data-testid="save-day-note-btn"
          >
            {isSaving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <StickyNote size={18} />
                Add Note
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayNoteModal;
